import { instance } from 'utils/api';

// GET /profile is the one v2 endpoint that returns SNAKE_CASE (toSnake on
// the BE) — matching the legacy GQL DoctorById shape, groups[] included.
// Locale-map jsonb fields (first_name, about, professional_title, …) come
// through as full Record<lang, string> maps — pickLocale before display.
export type ProfileGroupAttribute = {
  id: number;
  attribute_id: number;
  value?: string | null;
  row_index: number;
  sort_index: number;
  type?: string | null;
  slug?: string | null;
  name?: string | null;
};

export type ProfileGroup = {
  id: number;
  group_id: number;
  sort_index: number;
  name?: string | null;
  attributes: ProfileGroupAttribute[];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DoctorProfile = Record<string, any> & {
  id: number;
  groups: ProfileGroup[];
};

// The groups[] locale comes from the `locale` header (LanguageInterceptor),
// NOT from a query param — pass the DATA locale explicitly.
export const fetchProfile = async (lang?: string): Promise<DoctorProfile> => {
  const res = await instance.get<DoctorProfile>('profile', {
    headers: lang ? { locale: lang } : undefined
  });

  return res.data;
};

export type DoctorSpecialtyRow = {
  id: number;
  specialtyId?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  specialty?: { id: number; name?: any } | null;
};

export const fetchDoctorSpecialties = async (): Promise<
  DoctorSpecialtyRow[]
> => {
  const res = await instance.get<DoctorSpecialtyRow[]>('profile/specialties');

  return res.data || [];
};

export type PlanRow = {
  id: number;
  name: string;
  description?: string | null;
  price: number;
  // v2 wire is text ('video_call' | 'report'); legacy GQL was numeric 1 | 2
  type: string | number;
  duration?: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  features?: Record<string, any> | null;
  status?: string;
};

export const fetchPlans = async (): Promise<PlanRow[]> => {
  const res = await instance.get<PlanRow[]>('profile/plans');

  return res.data || [];
};

export const updatePassword = (payload: {
  currentPassword: string;
  newPassword: string;
}) => instance.post('profile/password', payload);

// PUT /profile — partial. `settings` is MERGED server-side (auth2fa
// preserved), so send just the changed keys. Locale-map fields
// (firstName/lastName/professionalTitle/about) accept a plain string
// (merged under `lang`) or a full Record<lang,string>.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateProfile = (payload: Record<string, any>) =>
  instance.put('profile', payload);

export const updateSettings = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  settings: Record<string, any>
) => instance.put('profile', { settings });

// PUT /profile/working-hours — whole-blob replace of the day-keyed jsonb
// { sunday: { enabled, hours: [{start,end}] }, ... }.
export const updateWorkingHours = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workingHours: Record<string, any>
) => instance.put('profile/working-hours', { working_hours: workingHours });

// PUT /profile/payment-account — upsert (v2 collapses legacy create/update).
// snake_case fields match the entity + the existing FE form.
export type PaymentAccountPayload = {
  account_number?: string;
  bank_name?: string;
  billing_address?: string;
  holder_name?: string;
  signature?: string;
};

export const upsertPaymentAccount = (payload: PaymentAccountPayload) =>
  instance.put('profile/payment-account', payload);

// Wire is the camelCase PaymentAccount entity row (or null when none).
export type PaymentAccountRow = {
  id: number;
  accountNumber?: string | null;
  bankName: string;
  billingAddress: string;
  holderName: string;
  signature: string;
  isDefault?: boolean;
};

export const fetchPaymentAccount =
  async (): Promise<PaymentAccountRow | null> => {
    const res = await instance.get<PaymentAccountRow | null>(
      'profile/payment-account'
    );

    return res.data ?? null;
  };
